import {
  AttachmentPreview,
  Box,
  RoleGuard,
  Table,
  useTable,
} from 'portal-commons';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { faFileMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Backdrop,
  Container,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';

import VettingDetailsRowV2 from './VettingDetailsRowV2';
import { TABLE_COLUMNS, VettingRecordAction } from './constants';
import AppealHistoryModal from '../AppealHistoryModal';
import AppealModal from '../AppealModal';
import AppealRequestConfirmationModal from '../AppealRequestConfirmationModal';
import AppealRequestModal from '../AppealRequestModal';
import AuthplusAppealModal from '../AuthplusAppealModal';
import { RcsVetAppealModal } from '../RcsVet';
import {
  getVettingRecords,
  requestAuthplusAppeal,
  requestRcsAppeal,
  resend2FA,
} from '../../apis';
import {
  downloadAttachmentApi,
  downloadAttachmentToDesktop,
  requestStandardVettingAppeal,
} from '../../apiServices';
import {
  AppealRequestData,
  BrandDetail,
  BrandFeedback,
  ExtVettingRecord,
  ModalType,
  VettingAppeal,
} from '../../types';
import {
  filterAppealCategoriesByVettingType,
  getAppealCategoryIdGroups,
} from '../../utils';
import { VettingType } from '../../../CSP-Form/constants';
import {
  selectAppealCategories,
  selectVettingSettings,
} from '../../../CSP-Form/selectors';
import Spinner from '../../../Campaigns/components/Spinner';
import { toastFlashMessage } from '@/utils';
import { WithFile } from '@/types';
import { Attachment } from '@/apis/core/types';
import { getExtVettingReport } from '@/apis/services';

const useStyles = makeStyles(() => ({
  root: {},
  modal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 430,
    height: 226,
    background: '#FFFFFF',
  },
}));

type stateObject = {
  [key: string]: boolean | number;
};

type AppealsByVettingId = {
  [key: string]: VettingAppeal;
};

type AttachmentExtras = WithFile<Attachment>;

export interface BrandVettingDetailsBlockHandle {
  reloadVettingRecords: () => void;
}

interface Props {
  brand: BrandDetail;
  appeals: VettingAppeal[];
  feedbackInfo: BrandFeedback[];
  onAppealCreate: (_appeal: VettingAppeal) => void;
  handleState: (_obj: stateObject) => void;
  actionGuard?: (func: (...args: any[]) => any) => any;
}

enum Scene {
  None = 'none',
  // Appeal = 'appeal',
  // AppealRequest = 'appealRequest',
  // AppealHistory = 'appealHistory',
  RcsAppeal = 'rcsAppeal',
  AuthplusAppeal = 'authplugAppeal',
}

const BrandVettingDetailsBlock = forwardRef<
  BrandVettingDetailsBlockHandle,
  Props
>(
  (
    {
      brand,
      appeals = [],
      onAppealCreate,
      handleState,
      actionGuard = (func: (...args: any[]) => any) =>
        (...args: any[]) =>
          func(...args),
    },
    ref
  ) => {
    const classes = useStyles();

    const appealCategories = useSelector(selectAppealCategories);
    const vettingSettings = useSelector(selectVettingSettings);
    const [appealOpen, setAppealOpen] = useState(false);
    const [appealScoreName, setAppealScoreName] = useState('');
    const [appealCompanyName, setAppealCompanyName] = useState('');
    const [appealContactEmail, setAppealContactEmail] = useState('');
    const [appealRequestOpen, setAppealRequestOpen] = useState(false);
    const [appealRequestRecord, setAppealRequestRecord] =
      useState<ExtVettingRecord | null>(null);
    const [appealRequestData, setAppealRequestData] =
      useState<AppealRequestData | null>(null);
    const [appealRequestConfirmationOpen, setAppealRequestConfirmationOpen] =
      useState(false);
    const [
      appealRequestConfirmationDisabled,
      setAppealRequestConfirmationDisabled,
    ] = useState(false);
    const [appealHistoryOpen, setAppealHistoryOpen] = useState(false);
    const [appealHistoryId, setAppealHistoryId] = useState<string>('');
    const [viewReport, setViewReport] = useState<AttachmentExtras>();
    const [loadingReport, setLoadingReport] = useState(false);
    const [latestAppeals, setLatestAppeals] = useState<AppealsByVettingId>({});
    const [scene, setScene] = useState<Scene>(Scene.None);
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const standardAppealCategories = useMemo(
      () =>
        filterAppealCategoriesByVettingType(
          appealCategories,
          VettingType.Standard
        ),
      [appealCategories]
    );
    const {
      validAppealCategoryIds: validStandardAppealCategoryIds,
      disabledAppealCategoryIds: disabledStandardAppealCategoryIds,
    } = useMemo(
      () =>
        getAppealCategoryIdGroups(vettingSettings, brand, VettingType.Standard),
      [vettingSettings, brand]
    );
    const rcsAppealCategories = useMemo(
      () =>
        filterAppealCategoriesByVettingType(appealCategories, VettingType.RCS),
      [appealCategories]
    );
    const {
      validAppealCategoryIds: validRcsAppealCategoryIds,
      disabledAppealCategoryIds: disabledRcsAppealCategoryIds,
    } = useMemo(
      () => getAppealCategoryIdGroups(vettingSettings, brand, VettingType.RCS),
      [vettingSettings, brand]
    );
    const authplusAppealCategories = useMemo(
      () =>
        filterAppealCategoriesByVettingType(
          appealCategories,
          VettingType.Authplus
        ),
      [appealCategories]
    );
    const {
      validAppealCategoryIds: validAuthplusAppealCategoryIds,
      disabledAppealCategoryIds: disabledAuthplusAppealCategoryIds,
    } = useMemo(
      () =>
        getAppealCategoryIdGroups(vettingSettings, brand, VettingType.Authplus),
      [vettingSettings, brand]
    );
    const appealHistoryItem = appealHistoryId
      ? latestAppeals[appealHistoryId]
      : undefined;

    const table = useTable<ExtVettingRecord>(
      async () => {
        const response =
          brand.uid != null ? await getVettingRecords(brand.uid) : [];
        return {
          data: response,
          total: response.length,
          page: 1,
          rowsPerPage: response.length,
        };
      },
      {
        deps: [brand.uid],
        inMemory: true,
      }
    );

    const handleAppealClick = (item: ExtVettingRecord) => {
      if (item.vettingClass === 'RCS') {
        setScene(Scene.RcsAppeal);
        setAppealRequestRecord(item);
        return;
      }

      if (item.evpUid === 'AEGIS') {
        if (item.vettingClass === 'STANDARD') {
          setAppealRequestOpen(true);
          setAppealRequestRecord(item);
        } else if (item.vettingClass === 'AUTHPLUS') {
          setScene(Scene.AuthplusAppeal);
          setAppealRequestRecord(item);
        } else {
          setAppealOpen(true);
          setAppealScoreName('AI3MS Score™');
          setAppealCompanyName('Aegis Mobile LLC');
          setAppealContactEmail('appeals@aegismobile.com');
        }
      } else if (item.evpUid === 'WMC') {
        setAppealOpen(true);
        setAppealScoreName('RISQ Score');
        setAppealCompanyName('WMC Global');
        setAppealContactEmail('support@risqscore.zendesk.com');
      }
    };

    const handleAppealHistoryClick = (item: ExtVettingRecord) => {
      setAppealHistoryOpen(true);
      setAppealHistoryId(item.vettingId);
    };

    useEffect(() => {
      const tmpLatestAppeals: AppealsByVettingId = {};
      appeals.forEach((appeal: VettingAppeal) => {
        if (!tmpLatestAppeals[appeal.vettingId]) {
          tmpLatestAppeals[appeal.vettingId] = appeal;
        }
      });
      setLatestAppeals(tmpLatestAppeals);
    }, [appeals]);

    useImperativeHandle(ref, () => ({
      reloadVettingRecords: () => {
        table.reload();
      },
    }));

    const handleViewReport = async (item: ExtVettingRecord) => {
      try {
        setViewReport(undefined);
        setLoadingReport(true);
        const result = await getExtVettingReport(item.brandUid, item.vettingId);
        if (!result) return;

        const response = await downloadAttachmentApi(result.uuid);
        if (!response) return;

        setViewReport({
          ...result,
          file: new File([response], result.fileName, {
            type: 'application/pdf',
          }),
          mimeType: 'application/pdf',
        } as AttachmentExtras);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoadingReport(false);
      }
    };

    const handleDownloadReport = async (item: ExtVettingRecord) => {
      try {
        const result = await getExtVettingReport(item.brandUid, item.vettingId);
        if (!result) return;

        await downloadAttachmentToDesktop(result.uuid, result.fileName);
      } catch (error) {
        // console.log(error);
      }
    };

    const handleResend2FA = async (item: Record<string, any>) => {
      const params = { vettingClass: item.vettingClass };
      const response = await resend2FA(item.brandUid, params);
      if (response) {
        toastFlashMessage('2FA email sent successfully.', 'success');
      }
    };

    const handleVettingActionClick = (
      action: VettingRecordAction,
      record: ExtVettingRecord
    ) => {
      switch (action) {
        case VettingRecordAction.Appeal:
          handleAppealClick(record);
          break;
        case VettingRecordAction.ViewAppealHistory:
          handleAppealHistoryClick(record);
          break;
        case VettingRecordAction.ViewReport:
          handleViewReport(record);
          break;
        case VettingRecordAction.DownloadReport:
          handleDownloadReport(record);
          break;
        case VettingRecordAction.Resend2FA:
          handleResend2FA(record);
          break;
        default:
          break;
      }
    };

    const handleRcsVetAppealSubmit = async (data: AppealRequestData) => {
      if (appealRequestRecord) {
        setModalSubmitting(true);
        const appeal = await requestRcsAppeal(
          appealRequestRecord.brandUid,
          appealRequestRecord.evpUid,
          appealRequestRecord.vettingId,
          data.categories,
          data.explanation,
          data.attachments
        );
        if (appeal) {
          toastFlashMessage('RCS vet successfully appealed', 'success');
          onAppealCreate && onAppealCreate(appeal);
        }
        setModalSubmitting(false);
      }
      setScene(Scene.None);
    };

    return (
      <Grid item xs={12} data-testid="brandVettingDetailsBlock">
        <div className="vetting details">
          <div className="details-heading">
            <div className="title">
              <Box flexDirection="row" alignItems="center">
                <Box margin={{ right: 'xs' }}>
                  <FontAwesomeIcon icon={faFileMagnifyingGlass} size="xl" />
                </Box>
                <h3 className="heading1">Vetting Details</h3>
              </Box>
            </div>
            <ul className="list-inline vetting">
              <RoleGuard feature="brandDetail.applyForVetting">
                <li
                  onClick={actionGuard(() => {
                    handleState({
                      modalOpenFlag: true,
                      modalType: ModalType.newVetting,
                    });
                  })}
                  data-testid="brandVettingDetailsBlockApplyVettingButton"
                >
                  <p className="paragraph">
                    <a>Apply for Vetting</a>
                  </p>
                </li>
              </RoleGuard>
              <RoleGuard feature="brandDetail.importVetting">
                <li
                  onClick={actionGuard(() => {
                    handleState({
                      modalOpenFlag: true,
                      modalType: ModalType.importVetting,
                    });
                  })}
                  data-testid="brandVettingDetailsBlockImportVettingButton"
                >
                  <p className="paragraph">
                    <a>Import Vetting</a>
                  </p>
                </li>
              </RoleGuard>
            </ul>
          </div>
          <Grid
            container
            className="brand-vetting-details"
            data-testid="brandVettingDetailsBlockContainer"
          >
            <Table
              className="vetting-list"
              loading={false}
              headRows={TABLE_COLUMNS}
              disableHover
              tableData={table.tableData.map((r, idx) => (
                <VettingDetailsRowV2
                  key={idx}
                  record={r}
                  appeals={appeals}
                  onActionClick={handleVettingActionClick}
                  actionGuard={actionGuard}
                />
              ))}
              emptyState="no vettings to view"
            />
            <AppealModal
              open={appealOpen}
              scoreName={appealScoreName}
              companyName={appealCompanyName}
              contactEmail={appealContactEmail}
              handleClose={() => setAppealOpen(false)}
            />
            <AppealRequestModal
              open={appealRequestOpen}
              brand={brand}
              title="Standard Vet Appeal Request"
              message="You can submit an Appeal up to 45 days after the completion of a vet. Appeals are charged $11.00 regardless of the outcome. Do not use this for Identity Status appeals."
              categories={standardAppealCategories.filter((category) =>
                validStandardAppealCategoryIds.includes(
                  category.appealCategoryId
                )
              )}
              disabledCategoryIds={disabledStandardAppealCategoryIds}
              onClose={() => setAppealRequestOpen(false)}
              onSubmit={(data) => {
                setAppealRequestOpen(false);
                setAppealRequestData(data);
                setAppealRequestConfirmationOpen(true);
              }}
            />
            <AppealRequestConfirmationModal
              open={appealRequestConfirmationOpen}
              disabled={appealRequestConfirmationDisabled}
              onClose={() => setAppealRequestConfirmationOpen(false)}
              onSubmit={async () => {
                setAppealRequestConfirmationDisabled(true);
                try {
                  if (appealRequestRecord && appealRequestData) {
                    const appeal = await requestStandardVettingAppeal(
                      appealRequestRecord.brandUid,
                      appealRequestRecord.vettingId,
                      appealRequestData.categories,
                      appealRequestData.explanation,
                      appealRequestData.attachments
                    );
                    toastFlashMessage('Vetting Appeal Requested', 'success');
                    onAppealCreate && onAppealCreate(appeal);
                  }
                } catch (e) {
                  // Ignore
                }
                setAppealRequestConfirmationOpen(false);
                setAppealRequestConfirmationDisabled(false);
              }}
            />
            <AppealHistoryModal
              open={appealHistoryOpen && !!appealHistoryItem}
              title={
                appealHistoryItem?.vettingClass === 'RCS'
                  ? 'RCS Vet'
                  : appealHistoryItem?.vettingClass === 'AUTHPLUS'
                  ? 'Authentication+ Vet'
                  : 'Standard Vet'
              }
              appeal={latestAppeals[appealHistoryId]}
              appealCategories={
                appealHistoryItem?.vettingClass === 'RCS'
                  ? rcsAppealCategories
                  : appealHistoryItem?.vettingClass === 'AUTHPLUS'
                  ? authplusAppealCategories
                  : standardAppealCategories
              }
              onClose={() => setAppealHistoryOpen(false)}
              hiddenFields={
                appealHistoryItem?.vettingClass === 'RCS' ||
                appealHistoryItem?.vettingClass === 'AUTHPLUS'
                  ? ['taxExemptStatus', 'governmentEntity']
                  : []
              }
            />
            <RcsVetAppealModal
              open={scene === Scene.RcsAppeal}
              submitting={modalSubmitting}
              appealCategories={rcsAppealCategories.filter((category) =>
                validRcsAppealCategoryIds.includes(category.appealCategoryId)
              )}
              disabledCategoryIds={disabledRcsAppealCategoryIds}
              onClose={() => setScene(Scene.None)}
              onSubmit={handleRcsVetAppealSubmit}
              // FIXME : get the price and waiting time from api
              price={20}
              waitingTime="2 ~ 3 days"
            />
            <AuthplusAppealModal
              open={scene === Scene.AuthplusAppeal}
              submitting={modalSubmitting}
              categories={authplusAppealCategories.filter((category) =>
                validAuthplusAppealCategoryIds.includes(
                  category.appealCategoryId
                )
              )}
              disabledCategoryIds={disabledAuthplusAppealCategoryIds}
              onClose={() => setScene(Scene.None)}
              onSubmit={async (data) => {
                if (appealRequestRecord) {
                  const appeal = await requestAuthplusAppeal(
                    appealRequestRecord.brandUid,
                    appealRequestRecord.evpUid,
                    appealRequestRecord.vettingId,
                    data.categories,
                    data.explanation,
                    data.attachments
                  );
                  if (appeal) {
                    toastFlashMessage('Vetting Appeal Requested', 'success');
                    onAppealCreate && onAppealCreate(appeal);
                  }
                }
                setScene(Scene.None);
              }}
            />

            {viewReport?.file && (
              <AttachmentPreview
                file={viewReport.file}
                mimeType={viewReport.mimeType}
                onClose={() => setViewReport(undefined)}
              />
            )}
            <Backdrop
              style={{ zIndex: 1005 }}
              open={loadingReport}
              onClick={() => setViewReport(undefined)}
            >
              <Container classes={{ root: classes.modal }}>
                <Spinner />
                <Typography
                  variant="body1"
                  align="center"
                  style={{
                    marginTop: 40,
                    color: '#49535D',
                  }}
                >
                  Please wait while we load
                  <br />
                  your preview
                </Typography>
              </Container>
            </Backdrop>
          </Grid>
        </div>
      </Grid>
    );
  }
);

BrandVettingDetailsBlock.displayName = 'BrandVettingDetailsBlock';

export default BrandVettingDetailsBlock;
